import { getAmplitudeUserId, setAmplitudeUserId } from './client';

/** Identifies the Amplitude identity mutation that requires an assignment refresh. */
export type AmplitudeIdentityUpdateNotification =
  | { identityUpdatePromise: Promise<unknown>; type: 'identify' }
  | { type: 'user-id'; userId?: string };

type AmplitudeIdentityUpdateListener = (notification: AmplitudeIdentityUpdateNotification) => void;

const amplitudeIdentityUpdateListeners = new Set<AmplitudeIdentityUpdateListener>();
const AMPLITUDE_OPT_OUT_RESULT_CODE = 0;
const AMPLITUDE_OPT_OUT_RESULT_MESSAGE = 'Event skipped due to optOut config';
let pendingAmplitudeIdentityUpdate: Promise<unknown> | undefined;
let completedAmplitudeIdentityUpdateToReplay: AmplitudeIdentityUpdateNotification | undefined;

const publishCompletedAmplitudeIdentityUpdate = (
  notification: AmplitudeIdentityUpdateNotification
): void => {
  if (amplitudeIdentityUpdateListeners.size === 0) {
    completedAmplitudeIdentityUpdateToReplay = notification;
    return;
  }

  for (const listener of amplitudeIdentityUpdateListeners) {
    listener(notification);
  }
};

/** Returns the latest Identify operation while Amplitude is still applying it. */
export const getPendingAmplitudeIdentityUpdate = (): Promise<unknown> | undefined =>
  pendingAmplitudeIdentityUpdate;

/** Returns Amplitude's delivery result code when one is available. */
export const getAmplitudeIdentityUpdateResultCode = (result: unknown): number | undefined => {
  if (typeof result !== 'object' || result === null) {
    return;
  }

  const code = Reflect.get(result, 'code');
  return typeof code === 'number' ? code : undefined;
};

/** Returns whether Amplitude confirmed delivery of an identity update. */
export const isSuccessfulAmplitudeIdentityUpdateResult = (result: unknown): boolean =>
  getAmplitudeIdentityUpdateResultCode(result) === 200;

/** Returns whether Amplitude skipped Identify delivery because analytics tracking is opted out. */
export const isAmplitudeIdentityUpdateOptOutResult = (result: unknown): boolean => {
  if (getAmplitudeIdentityUpdateResultCode(result) !== AMPLITUDE_OPT_OUT_RESULT_CODE) {
    return false;
  }

  if (typeof result !== 'object' || result === null) {
    return false;
  }

  return Reflect.get(result, 'message') === AMPLITUDE_OPT_OUT_RESULT_MESSAGE;
};

/** Notifies subscribers after Amplitude accepts an Identify update. */
export const notifyWhenAmplitudeIdentityUpdateCompletes = (
  identityUpdatePromise: Promise<unknown>
): void => {
  pendingAmplitudeIdentityUpdate = identityUpdatePromise;
  void identityUpdatePromise.then(
    result => {
      // Ignore a superseded Identify: a newer update is already pending, so publishing this
      // stale completion would announce an out-of-date identity.
      if (pendingAmplitudeIdentityUpdate !== identityUpdatePromise) {
        return;
      }
      pendingAmplitudeIdentityUpdate = undefined;
      if (isSuccessfulAmplitudeIdentityUpdateResult(result)) {
        publishCompletedAmplitudeIdentityUpdate({ identityUpdatePromise, type: 'identify' });
      }
    },
    () => {
      if (pendingAmplitudeIdentityUpdate === identityUpdatePromise) {
        pendingAmplitudeIdentityUpdate = undefined;
      }
    }
  );
};

/** Notifies subscribers after Amplitude synchronously applies a user ID change. */
export const notifyAmplitudeUserIdChanged = (userId?: string): void => {
  publishCompletedAmplitudeIdentityUpdate({ type: 'user-id', userId });
};

/** Applies actual user ID changes and always queues null clears for pre-initialization safety. */
export const updateAmplitudeUserId = (userId: string | null): void => {
  // Treat any falsy id (null or empty string) as a clear, and always apply clears so logout can
  // queue the SDK setter even before initialization exposes the current user id.
  const nextUserId = userId || undefined;
  if (nextUserId !== undefined && getAmplitudeUserId() === nextUserId) {
    return;
  }

  setAmplitudeUserId(nextUserId);
  notifyAmplitudeUserIdChanged(nextUserId);
};

/** Subscribes to completed Amplitude Identify updates. */
export const subscribeToAmplitudeIdentityUpdates = (
  listener: AmplitudeIdentityUpdateListener
): (() => void) => {
  amplitudeIdentityUpdateListeners.add(listener);
  if (completedAmplitudeIdentityUpdateToReplay) {
    const completedIdentityUpdate = completedAmplitudeIdentityUpdateToReplay;
    completedAmplitudeIdentityUpdateToReplay = undefined;
    listener(completedIdentityUpdate);
  }

  return () => {
    amplitudeIdentityUpdateListeners.delete(listener);
  };
};
